import { useMutation, useQuery } from "@apollo/client";
import React, { FC, useEffect, useState } from "react";
import {
    Button,
    Col,
    FormControl,
    InputGroup,
    Modal,
    Row
} from "react-bootstrap";
import {
    ADMIN_DELETE_ANALYTICS_LIB_EVENT,
    ADMIN_UPDATE_ANALYTICS_LIB_EVENT
} from "../../../graphql/mutations";
import { GET_ANALYTICS_EVENTS_BY_ORGANIZATION } from "../../../graphql/queries";
import {
    AnalyticsEventByOrgVariables,
    AnalyticsEventsById,
    AnalyticsEventSchema,
    AnalyticsEventVariables,
    UpdateAnalyticsLibEventResult
} from "../../../models/types";
import { AdminAnalyticsEventLibraryItem } from "../../views/admin/AdminAnalyticsEventLibraryItem";
import { useLocalState } from "../../../graphql/hooks";

interface Props {
    organizationName: string;
    organizationId: number;
    show: boolean;
    onClose: () => void;
}

export const AnalyticsLibEventsPerOrganizationModal: FC<Props> = ({
    organizationName,
    organizationId,
    show,
    onClose
}) => {
    const { useDarkMode } = useLocalState();
    const [searchTerm, setSearchTerm] = useState("");

    const { data: { getEventsByOrgId } = {} } = useQuery<
        AnalyticsEventsById,
        AnalyticsEventByOrgVariables
    >(GET_ANALYTICS_EVENTS_BY_ORGANIZATION, {
        fetchPolicy: "network-only",
        variables: {
            organizationId: organizationId
        },
        skip: organizationId === 0
    });

    useEffect(() => {
        if (!show) {
            setSearchTerm("");
        }
    }, [show]);

    const [removeAnalyticsLibEventMutation] = useMutation<
        { adminDeleteAnalyticsLibEvent: { success: boolean } },
        { eventId: number }
    >(ADMIN_DELETE_ANALYTICS_LIB_EVENT);

    const [analyticsLibEventMutation] = useMutation<
        UpdateAnalyticsLibEventResult,
        AnalyticsEventVariables
    >(ADMIN_UPDATE_ANALYTICS_LIB_EVENT);

    const removeLibEvent = async (eventId: number) => {
        try {
            await removeAnalyticsLibEventMutation({
                variables: {
                    eventId: eventId
                },
                update: (cache, { data }) => {
                    const currentSavedLibEventsData =
                        cache.readQuery<AnalyticsEventsById>({
                            query: GET_ANALYTICS_EVENTS_BY_ORGANIZATION,
                            variables: {
                                organizationId: organizationId
                            }
                        });
                    const currentSavedLibEvents =
                        currentSavedLibEventsData?.getEventsByOrgId;
                    if (
                        currentSavedLibEvents &&
                        data &&
                        data.adminDeleteAnalyticsLibEvent
                    ) {
                        const newQueries = currentSavedLibEvents.filter(
                            element => element.id !== eventId
                        );
                        cache.writeQuery<AnalyticsEventsById>({
                            query: GET_ANALYTICS_EVENTS_BY_ORGANIZATION,
                            data: { getEventsByOrgId: newQueries },
                            variables: {
                                organizationId: organizationId
                            }
                        });
                    }
                }
            });
        } catch (error) {
            console.log("[DEBUG] removeLibEvent error: ", error);
        }
    };

    const applyLibEventUpdate = async (eventId: number, frozen: boolean) => {
        try {
            await analyticsLibEventMutation({
                variables: {
                    eventId: eventId,
                    frozen: frozen
                }
            });
        } catch (error) {
            console.log("[DEBUG] applyLibEventUpdate error ", error);
        }
    };

    const filteredEvents = getEventsByOrgId?.filter(event =>
        event.eventName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const eventsList = filteredEvents?.map(
        (event: AnalyticsEventSchema, index: number) => (
            <Col xl={6} key={event.eventName + index}>
                <AdminAnalyticsEventLibraryItem
                    organizationName={organizationName}
                    event={event}
                    applyLibEventUpdate={applyLibEventUpdate}
                    removeLibEvent={removeLibEvent}
                />
            </Col>
        )
    );

    return (
        <Modal
            show={show}
            onHide={onClose}
            backdrop="static"
            keyboard={true}
            size="xl"
            className={useDarkMode ? "modal-dark" : ""}
        >
            <Modal.Header closeButton>
                <Modal.Title>{organizationName}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="px-2">
                <Row>
                    <Col xs="auto" className="pb-2 ms-2">
                        <InputGroup>
                            <InputGroup.Text>Search</InputGroup.Text>
                            <FormControl
                                placeholder="Search event..."
                                value={searchTerm}
                                onChange={event =>
                                    setSearchTerm(event.target.value)
                                }
                            />
                        </InputGroup>
                    </Col>
                </Row>
                {filteredEvents && filteredEvents.length > 0 ? (
                    <Row>{eventsList}</Row>
                ) : (
                    <div className="d-flex justify-content-center py-3">
                        <p>No events found.</p>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-center">
                <Button
                    variant="secondary w-50"
                    onClick={(e: React.MouseEvent<HTMLElement>) => {
                        e.stopPropagation();
                        onClose();
                    }}
                >
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
